import React from "react";
import { Gauge, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import {
  readinessComponents,
  computeReadinessScore,
  getReadinessBand,
} from "@/data/geoEsgData";

export default function ReadinessScore({ compact = false }) {
  const score = computeReadinessScore();
  const band = getReadinessBand(score);

  // SVG gauge
  const radius = compact ? 52 : 68;
  const stroke = 8;
  const norm = radius - stroke;
  const circ = norm * 2 * Math.PI;
  const offset = circ - (score / 100) * circ;

  return (
    <div
      data-testid="readiness-score"
      className={`bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg p-5 md:p-6 text-white relative overflow-hidden grain ${compact ? "" : ""}`}
    >
      <div className="flex items-start gap-5">
        {/* Gauge */}
        <div className="relative flex-shrink-0" style={{ width: radius * 2, height: radius * 2 }}>
          <svg width={radius * 2} height={radius * 2} className="-rotate-90">
            <circle
              cx={radius} cy={radius} r={norm}
              fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={stroke}
            />
            <circle
              cx={radius} cy={radius} r={norm}
              fill="none" stroke={band.color} strokeWidth={stroke}
              strokeDasharray={circ} strokeDashoffset={offset}
              strokeLinecap="round"
              style={{ transition: "stroke-dashoffset 0.6s ease" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="font-display text-3xl md:text-4xl font-bold tabular-nums" style={{ color: band.color }}>
              {score}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-white/60">/ 100</div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Gauge size={14} className="text-amber-400" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 font-semibold">
              Mine Sustainability & Readiness
            </div>
          </div>
          <h3
            data-testid="readiness-band"
            className="font-display text-xl md:text-2xl font-semibold tracking-tight"
            style={{ color: band.color }}
          >
            {band.label}
          </h3>
          <p className="text-xs md:text-sm text-slate-300 mt-1.5 leading-relaxed">{band.description}</p>

          {!compact && (
            <Link
              to="/geo-esg"
              data-testid="readiness-deep-dive"
              className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-amber-400 hover:text-amber-300 group"
            >
              Open full sustainability view
              <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
          )}
        </div>
      </div>

      {/* Component breakdown */}
      {!compact && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mt-5 pt-5 border-t border-white/10" data-testid="readiness-breakdown">
          {readinessComponents.map((c) => (
            <div key={c.dimension} className="text-center">
              <div className="text-[9px] uppercase tracking-wider text-white/50 font-semibold">{c.dimension}</div>
              <div className="font-display text-lg font-bold mt-0.5" style={{ color: c.color === "#0F172A" ? "#FFFFFF" : c.color }}>
                {c.score}
              </div>
              <div className="h-1 bg-white/10 rounded-full overflow-hidden mt-1">
                <div className="h-full" style={{ width: `${c.score}%`, background: c.color === "#0F172A" ? "#FCD34D" : c.color }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
